import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { FC, useMemo } from 'react';
import { useQuery } from 'react-query';
import { ITerritory, IResponse } from '../../models';
import { getTerritories } from '../../fetch';

type TerritoryDropdownProps<TMultiple = boolean> = TMultiple extends true
  ? {
      multiple?: TMultiple;
      value: any[];
      handleChange: (val: any) => void;
      isDisabled?: boolean;
      setOptions?: (val: any) => void;
      isRequired?: boolean;
      message?: string;
      hasError?: boolean;
      returnTerritoryName?: boolean;
      handleClear?: (val: any) => void;
      apiParams?: any;
    }
  : {
      multiple?: TMultiple;
      value: string | number;
      handleChange: (val: any) => void;
      isDisabled?: boolean;
      setOptions?: (val: any) => void;
      isRequired?: boolean;
      message?: string;
      hasError?: boolean;
      returnTerritoryName?: boolean;
      handleClear?: (val: any) => void;
      apiParams?: any;
    };

export const TerritoryDropdown: FC<TerritoryDropdownProps> = ({
  isDisabled,
  isRequired,
  value,
  handleChange,
  setOptions,
  message,
  hasError,
  returnTerritoryName,
  multiple,
  handleClear,
  apiParams
}) => {
  const { isLoading: isLoadingTerritories, data: territoriesData } = useQuery<
    IResponse<ITerritory[]>,
    Error
  >(['territories-filters', apiParams], () => getTerritories(apiParams), {
    notifyOnChangeProps: 'tracked',
    onSuccess: d => setOptions?.(d)
  });
  const territories = useMemo(() => territoriesData?.records ?? [], [territoriesData]);
  return (
    <FormControl fullWidth variant="outlined" size="small" required={isRequired} error={hasError}>
      <InputLabel id={`TerritoryId`}>
        {isLoadingTerritories ? 'Loading...' : 'Territory'}
      </InputLabel>
      <Select
        disabled={isDisabled || isLoadingTerritories}
        name={`TerritoryId`}
        labelId={`TerritoryId`}
        id={`TerritoryId`}
        value={value}
        multiple={multiple}
        size="small"
        MenuProps={{
          sx: {
            maxHeight: '30rem'
          }
        }}
        endAdornment={
          value &&
          handleClear && (
            <InputAdornment
              position="end"
              sx={{
                position: 'absolute',
                right: 25,
                cursor: 'pointer'
              }}
            >
              <IconButton sx={{ padding: 0 }} title="Clear" onClick={handleClear}>
                <Close fontSize="small" />
              </IconButton>
            </InputAdornment>
          )
        }
        onChange={({ target: { value } }) => {
          if (Array.isArray(value)) {
            handleChange(value as any[]);
          } else {
            handleChange((returnTerritoryName ? value : Number(value)) as string | number);
          }
        }}
      >
        {!isLoadingTerritories && territories && territories.length === 0 && (
          <MenuItem disabled>No territories available</MenuItem>
        )}
        {!isLoadingTerritories &&
          territories &&
          territories.map((territory, index) => {
            return (
              <MenuItem
                key={`${index}`}
                value={returnTerritoryName ? territory.name : territory.territoryId}
              >
                {territory.name}
              </MenuItem>
            );
          })}
      </Select>
      {message && <FormHelperText error>{message}</FormHelperText>}
    </FormControl>
  );
};
